import { Accordion } from "@components/atoms/Accordion"
import { Mapbox } from "@components/atoms/Mapbox"
import { PageHeader } from "@components/page/PageHeader"
import { ButtonFragment } from "@generated-types"
import { defineBlock, EditableText } from "eddev/blocks"

export const meta: BlockMeta = {
  title: "Venue Details",
  icon: "location",
  postMetaBlock: {
    fieldName: "venueHeader",
    postTypes: ["venue"],
  },
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("venue/venue-header", (props) => {
  const address = JSON.parse(props.venue?.info?.address || "{}")
  const links = [] as Partial<ButtonFragment>[]
  links.push({ title: "Visit website", url: props.venue?.info?.website!, target: "_blank" })
  if (address.Street) {
    links.push({
      title: "View on Google Maps",
      url: "https://www.google.com/maps/search/?api=1&query=" + address.Street,
      target: "_blank",
    })
  }
  links.push({ title: "Films & events", url: "/program", target: "" })

  const sections: { label: string; content: string }[] = [
    {
      label: "Transport",
      content: props.venue?.descriptions?.transport || "",
    },
    {
      label: "Accessibility",
      content: props.venue?.descriptions?.accessibility || "",
    },
    {
      label: "Offers",
      content: props.venue?.descriptions?.offer || "",
    },
  ].filter((section) => !!section.content)

  return (
    <div className="w-full">
      <PageHeader
        mixpanelArea="Venue Page"
        breadcrumbs={props.venue?.breadcrumbs!}
        title={props.venue?.title}
        description={<div dangerouslySetInnerHTML={{ __html: props.venue?.descriptions?.description || "" }} />}
        image={props.image || undefined}
        links={links}
      />

      {sections.length > 0 && (
        <div className="grid-auto mb-8 mt-6">
          <div className="col-subpage-content-grid flex flex-col gap-3 mt-8 md:mt-0">
            {sections.map((section, i) => (
              <div key={i} className="w-full">
                <Accordion title={section.label}>
                  <div className="wysiwyg" dangerouslySetInnerHTML={{ __html: section.content }} />
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid-auto mb-8">
        <div className="col-span-12 sm:col-2-8 md:col-0-5 pointer-events-auto">
          <EditableText
            as="p"
            store="visit-title"
            className="type-title-m text-balance"
            placeholder={`Visiting ${props.venue?.title}`}
            defaultValue={`Visiting ${props.venue?.title}`}
          />
        </div>
        <div className="col-subpage-content-grid">
          <div className="w-full type-label-m mb-8">
            {address.Street && (
              <div className="flex justify-between w-full border-b border-bg-mid py-3">
                <div>Address</div>
                <div>{address.Street}</div>
              </div>
            )}
            {props.venue?.info?.phone && (
              <div className="flex justify-between w-full border-b border-bg-mid py-3">
                <div>Phone</div>
                <div className="text-fg-secondary">
                  <a href={`tel:${props.venue?.info?.phone}`}>{props.venue?.info?.phone}</a>
                </div>
              </div>
            )}
            {props.venue?.info?.website && (
              <div className="flex justify-between w-full border-b border-bg-mid py-3">
                <div>Website</div>
                <div className="text-fg-secondary">
                  <a href={props.venue?.info?.website} target="_blank" rel="noreferrer">
                    {props.venue?.info?.website}
                  </a>
                </div>
              </div>
            )}
          </div>

          {address.Location?.Latitude && address.Location?.Longitude && (
            <div className="w-full mb-8">
              <Mapbox
                lat={address.Location.Latitude}
                long={address.Location.Longitude}
                link={"https://www.google.com/maps/search/?api=1&query=" + address.Street}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
